/**
 * Frontend Error Handler Utility
 *
 * Provides centralized error handling for API responses and token expiration detection.
 */

export interface ApiError {
  error: string;
  code?: string;
  details?: string;
}

// F13-6 (Korrekturlauf 09.10.2026): eine Recherchesperre ist ein Zustand des Kontos, kein Fehler der Recherche. Die Codes
// kommen aus backend/src/middleware/auth.ts:140-172 und backend/src/administration/researchUsage.ts:106-111. Grenzen und
// Kosten (429 COST_LIMIT, 409 COST_UNCONFIGURED) gehören nicht dazu: dort bleibt der Hinweis unter der Frage.
// Profil-Nachbau F-16 (PF-773, BAU-RECHT.md 2.3): dazu 403 AVV_REQUIRED, solange die Firma den AVV nicht angenommen hat.
// F-17 (BAU-RECHT.md 3.2): ebenso 403 USAGE_POLICY_REQUIRED, solange das Konto die Nutzungsrichtlinie der Firma nicht bestätigt hat.
export const RECHERCHE_SPERRCODES = ['PAYMENT_OVERDUE', 'SEAT_REQUIRED', 'SUBSCRIPTION_EXPIRED', 'ORGANIZATION_BLOCKED', 'PLATFORM_RESEARCH_BLOCKED', 'AVV_REQUIRED',
  'USAGE_POLICY_REQUIRED'] as const;

export function istRechercheSperre(error: unknown): boolean {
  const response = (error as { response?: { status?: unknown; data?: { code?: unknown } | null } } | null | undefined)?.response;
  const code = response?.data?.code;
  return response?.status === 403 && typeof code === 'string' && (RECHERCHE_SPERRCODES as readonly string[]).includes(code);
}

export class ErrorHandler {
  /**
   * Extract user-friendly error message from API error response
   *
   * @param error - The error object from axios or other API calls
   * @returns User-friendly error message in German
   */
  static researchErrorMessage(error: any): string {
    // Server faults carry internal English texts (e.g. "V25 search failed"); access and budget errors are German and specific.
    if (error?.response?.status >= 500) return 'Die Recherche konnte gerade nicht ausgeführt werden. Bitte versuchen Sie es später erneut.';
    return ErrorHandler.handleApiError(error);
  }

  static handleApiError(error: any): string {
    // Check for API error message in response
    if (error.response?.data?.error) {
      return error.response.data.error;
    }

    // Check for network errors (no response from server)
    if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      return 'Netzwerkfehler. Bitte überprüfen Sie Ihre Internetverbindung.';
    }

    // Check for connection refused
    if (error.code === 'ECONNREFUSED' || error.message?.includes('ERR_CONNECTION_REFUSED')) {
      return 'Verbindung zum Server fehlgeschlagen. Bitte versuchen Sie es spaeter erneut.';
    }

    // Check for timeout
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return 'Zeitüberschreitung. Bitte versuchen Sie es erneut.';
    }

    // Fallback to generic error
    return 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.';
  }

  /**
   * Check if error is due to expired/invalidated token
   *
   * @param error - The error object from axios or other API calls
   * @returns True if token is expired or invalidated
   */
  static isTokenExpired(error: any): boolean {
    const code = error.response?.data?.code;
    return code === 'TOKEN_EXPIRED' || code === 'TOKEN_INVALIDATED' || code === 'TOKEN_INVALID';
  }

  /**
   * Check if error is an authentication error (401 or 403)
   *
   * @param error - The error object from axios or other API calls
   * @returns True if error is authentication-related
   */
  static isAuthError(error: any): boolean {
    return error.response?.status === 401 || error.response?.status === 403;
  }
}
